import type { BillableFormPluginProps, SaveCallbackData } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { MinPrice } from '@domains/subscription/priv/Billables/usecases/Price/min/min'
import type { BaseAlgorithm } from '@domains/subscription/priv/components/Confirmable/confirmable'
import { Stack, Typography } from '@mui/material'
import { useRef } from 'react'
import { useForm } from 'react-hook-form'

export interface MinPricePlugin<T extends BaseAlgorithm> {
  supports: (type: string) => boolean
  Component: React.ComponentType<MinBillableFormProps<T>>
}

export interface MinBillableFormProps<T extends BaseAlgorithm> extends BillableFormPluginProps {
  minPrice: MinPrice<T> | undefined
  onSubmit: (data: MinPrice<T>) => Promise<void>
}

export const MinPriceRow = ({
  minPrice,
  onRegisterSave,
  onSubmit,
  additionalPlugins,
  templateId,
  unit,
  onDataChange,
}: MinBillableFormProps<BaseAlgorithm>) => {
  const form = useForm<MinPrice<BaseAlgorithm>>({
    values: minPrice,
  })

  const allChangesRef = useRef<Record<string, SaveCallbackData>>({})

  const submitForm = async (values: MinPrice<BaseAlgorithm>) => {
    await onSubmit(values).then(() => {
      onRegisterSave({
        changes: [],
        save: () => form.handleSubmit(submitForm)(),
      })
    })
  }

  const handleFieldChanges = (leftOrRight: 'left' | 'right', callback: SaveCallbackData) => {
    allChangesRef.current[leftOrRight] = callback

    const allChanges = Object.values(allChangesRef.current).flatMap((c) => c.changes)
    const callbacks = Object.values(allChangesRef.current).map((c) => c.save)

    onRegisterSave({
      changes: allChanges,
      save: () => {
        callbacks.forEach((save) => save())
        return form.handleSubmit(submitForm)()
      },
    })
  }

  const getPluginForAlgorithm = (algorithm: BaseAlgorithm | undefined): React.ComponentType<BillableFormPluginProps> => {
    if (!algorithm) {
      return () => (
        <Stack
          data-testid="missing-tier-plugin"
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="p4Regular" sx={{ color: 'red' }}>
            Price does not exist for this billable row
          </Typography>
        </Stack>
      )
    }

    const plugin = additionalPlugins?.find((plugin) => plugin.supports(algorithm))

    if (!plugin || !plugin.FormComponent) {
      return () => (
        <Stack
          data-testid="missing-tier-plugin"
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="p4Regular" sx={{ color: 'red' }}>
            Backoffice does not support '{algorithm.type}' price types in this configuration
          </Typography>
        </Stack>
      )
    }

    return plugin.FormComponent
  }

  const LeftAlgorithm = getPluginForAlgorithm(minPrice?.lalgorithm)
  const RightAlgorithm = getPluginForAlgorithm(minPrice?.ralgorithm)

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="p3Regular">Left algorithm</Typography>
        <LeftAlgorithm
          price={minPrice?.lalgorithm}
          onRegisterSave={(changes) => handleFieldChanges('left', changes)}
          additionalPlugins={additionalPlugins}
          templateId={templateId}
          unit={unit}
          onDataChange={(data) => {
            form.setValue(`lalgorithm`, data)
            form.handleSubmit((data) => onDataChange?.(data))()
          }}
        />
      </Stack>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="p3Regular">Right algorithm</Typography>
        <RightAlgorithm
          price={minPrice?.ralgorithm}
          additionalPlugins={additionalPlugins}
          templateId={templateId}
          unit={unit}
          onRegisterSave={(changes) => handleFieldChanges('right', changes)}
          onDataChange={(data) => {
            form.setValue(`ralgorithm`, data)
            form.handleSubmit((data) => onDataChange?.(data))()
          }}
        />
      </Stack>
    </Stack>
  )
}

const noop = () => {}
